import { IDOMSymbolDetail } from '@aqx-core/common/api/dom';
import { ESymbolCategory, ISymbol, marketAPIController } from '@aqx-core/common/api/market';
import { EWatchlistCategory, watchlistAPIController } from '@aqx-core/common/api/watchlist';
import { marketViewController } from '@aqx-core/common/services/market/controller';
import { mixpanelAnalysisService } from '@aqx-core/common/services/mixpanel';
import { searchViewController } from '@aqx-core/common/services/search/controller';
import { tradeAnalysisViewController } from '@aqx-core/common/services/trade/analysis/controller';
import { IFavoriteSymbolCallback } from '@aqx-core/common/services/trade/watchlist/type';
import { socketService } from '@aqx-core/common/socket';
import {
  ISymbolTopicLivePrices,
  SYM_SUB_KEY_UITRADE_WATCHLIST_ITEM,
  symbolTopicSubscription
} from '@aqx-core/common/socket/subscriptions';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';
import { dispatchRefetchCurrentList } from '@aqx-core/common/utils/event';
import { ESortType, sortObjAscDescByKey } from '@aqx-core/common/utils/sorting';

import { companyService } from '../../company';
import { companyViewController } from '../../company/controller';
import { tradeAnalysisService } from '../analysis';
import { EUIBuySellType } from '../form/types';
import { tradeWatchlistService } from './service';

class TradeWatchlistViewController {
  private selectedWatchListFilter: EWatchlistCategory = EWatchlistCategory.FAVOURITES;
  private selectedSymbolCategory?: ESymbolCategory = undefined;

  private symbolTypeList: ESymbolCategory[] = [];

  private symbolToSubscribeSet = new Set<string>();

  private mappedAllSymbols: Record<string, ISymbol> = {};
  private mappedMyWatchlistSymbols: Record<string, ISymbol> = {};
  private mappedTopPickSymbols: Record<string, ISymbol> = {};
  private mappedTopGainerSymbols: Record<string, ISymbol> = {};
  private mappedTopLoserSymbols: Record<string, ISymbol> = {};

  private allSymbolIds: string[] = [];
  private myWatchlistIds: string[] = [];
  private topPickIds: string[] = [];
  private topGainerIds: string[] = [];
  private topLoserIds: string[] = [];

  private isCallingAllSymbolAPI = false;
  private pendingSelectFirstSymbolInAll = false;
  private initial = true;

  private pendingSymbolValidation: { symbols: ISymbol[] } | null = null;

  public init = async () => {
    if (this.initial) {
      this.getAllSymbols(true);
      this.getSymbolCategories();

      tradeWatchlistService.setSelectedWatchListFilter(EWatchlistCategory.FAVOURITES);
      this.selectedWatchListFilter = EWatchlistCategory.FAVOURITES;

      this.getWatchlistByFilter(this.selectedWatchListFilter, this.initial);
    }
  };

  public refreshCurrentWatchlist = async (options?: { allowFallback?: boolean }) => {
    const allowFallback = options?.allowFallback ?? true;
    await this.getWatchlistByFilter(this.selectedWatchListFilter, this.initial, { allowFallback });
  };

  public reset = () => {
    this.unsubscribeAllSymbolTopic();
  };

  public resetTradeWatchlistView = () => {
    tradeAnalysisService.setSymbolDetailId(undefined);
    tradeAnalysisService.setSymbolDetail(undefined);
    tradeWatchlistService.setSelectedWatchListFilter(EWatchlistCategory.FAVOURITES);
    this.selectedWatchListFilter = EWatchlistCategory.FAVOURITES;
    this.initial = true;
  };

  public setInitial = (value: boolean) => {
    this.initial = value;
  };

  public handleResetShouldRefetchFavourites = () => {
    tradeWatchlistService.setShouldRefetchFavourites(false);
  };

  public onFiltersChange = (tab: EWatchlistCategory, category: ESymbolCategory | undefined) => {
    this.selectedWatchListFilter = tab;
    tradeWatchlistService.setSelectedWatchListFilter(tab);
    tradeWatchlistService.setSelectedSymbolCategory(category);
  };

  // User Actions
  public onWatchlistFilterChanged = (filter: EWatchlistCategory, options?: { selectFirstSymbol?: boolean }) => {
    if (this.selectedWatchListFilter === filter) {
      return;
    }

    this.selectedWatchListFilter = filter;
    tradeWatchlistService.setSelectedWatchListFilter(filter);
    mixpanelAnalysisService.page.watchlistClick(filter);
    this.getWatchlistByFilter(this.selectedWatchListFilter, this.initial, {
      allowFallback: true,
      selectFirstSymbol: options?.selectFirstSymbol
    });
  };

  public onAllSymbolCategoryChanged = (category?: ESymbolCategory) => {
    if (!category) {
      this.selectedSymbolCategory = undefined;
    } else if (this.selectedSymbolCategory === category) {
      this.selectedSymbolCategory = undefined;
    } else {
      this.selectedSymbolCategory = category;
    }

    tradeWatchlistService.setSelectedSymbolCategory(this.selectedSymbolCategory);

    this.allSymbolsWithCategoryFiltered();
  };

  public onSymbolSelected = (id: string, redirected?: boolean) => {
    const selectedSymbol = redirected
      ? tradeWatchlistService.context.allWatchlistSymbols[id]
      : tradeWatchlistService.context.displayWatchlistSymbols[id];

    this.onSymbolSelectedWithDetail(id, selectedSymbol);
  };

  public onSymbolSelectedWithDetail = (
    id: string,
    selectedSymbol?: ISymbol | IDOMSymbolDetail,
    buySellType?: EUIBuySellType
  ) => {
    if (selectedSymbol) {
      tradeAnalysisViewController.setSymbolDetail({ buySellType, symbolId: id, symbol: selectedSymbol });
    } else {
      tradeAnalysisViewController.setSymbolDetail({ buySellType, symbolId: id });
    }
  };

  public onFavouriteSymbol = async (symbolId: string, favourite: boolean, callback?: IFavoriteSymbolCallback) => {
    if (!symbolId) {
      return;
    }

    if (favourite) {
      await this.favouriteSymbol(symbolId, callback);
    } else {
      await this.unfavouriteSymbol(symbolId, callback);
    }
  };

  public onPinSymbol = async (symbol: string, callback?: IFavoriteSymbolCallback) => {
    if (!symbol) {
      return;
    }
    await watchlistAPIController.pinSymbol(
      { symbol },
      {
        onSuccess: () => {
          if (this.selectedWatchListFilter === EWatchlistCategory.FAVOURITES) {
            this.getWatchlistByFilter(this.selectedWatchListFilter);
          }
          dispatchRefetchCurrentList();
          tradeWatchlistService.setShouldRefetchFavourites(true);
          callback?.onSuccess?.();
        },
        onError: () => {
          callback?.onError?.();
        }
      }
    );
  };

  public onUnpinSymbol = async (symbol: string, callback?: IFavoriteSymbolCallback) => {
    if (!symbol) {
      return;
    }
    await watchlistAPIController.unpinSymbol(
      { symbol },
      {
        onSuccess: () => {
          if (this.selectedWatchListFilter === EWatchlistCategory.FAVOURITES) {
            this.getWatchlistByFilter(this.selectedWatchListFilter);
          }
          dispatchRefetchCurrentList();
          tradeWatchlistService.setShouldRefetchFavourites(true);
          callback?.onSuccess?.();
        },
        onError: () => {
          callback?.onError?.();
        }
      }
    );
  };

  public onUpdateWatchlistSortOrder = async (
    watchList: string[],
    callback?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    if (!watchList?.length) {
      return;
    }
    await watchlistAPIController.updateWatchlistSortOrder(
      { watchList },
      {
        onSuccess: () => {
          if (this.selectedWatchListFilter === EWatchlistCategory.FAVOURITES) {
            this.getWatchlistByFilter(this.selectedWatchListFilter);
          }
          dispatchRefetchCurrentList();
          tradeWatchlistService.setShouldRefetchFavourites(true);
          callback?.onSuccess?.();
        },
        onError: () => {
          callback?.onError?.();
        }
      }
    );
  };

  public updateSelectedSymbolWatchState = (symbolId: string, favourite: boolean) => {
    const symbolDetail = tradeAnalysisService.context.symbolDetail;
    if (symbolDetail?.symbol === symbolId) {
      symbolDetail.watch = favourite;
      tradeAnalysisService.setSymbolDetail({ ...symbolDetail, watch: favourite });
    }
  };

  // API Calls
  public getSymbolCategories = async () => {
    await marketAPIController.getMarketSymbolCategories({
      onSuccess: (result) => {
        this.symbolTypeList = result?.symbolTypeList ?? [];
        tradeWatchlistService.setSymbolCategories(this.symbolTypeList);
      },
      onError: () => {}
    });
  };

  private getAllSymbols = async (initial?: boolean, selectFirstSymbol?: boolean) => {
    if (this.isCallingAllSymbolAPI) {
      // Queue selectFirstSymbol for when the in-flight request completes
      // This handles the race condition where init() triggers getAllSymbols,
      // but fallback to ALL category occurs before it finishes
      if (selectFirstSymbol) {
        this.pendingSelectFirstSymbolInAll = true;
      }
      return;
    }

    tradeWatchlistService.setAllSymbolsLoading(!initial);
    this.isCallingAllSymbolAPI = true;

    await marketAPIController.getAllSymbolsWithoutHideEffect({
      onSuccess: (symbols) => {
        if (symbols && symbols.length > 0) {
          const originalSymbols = [...symbols];
          // Apply alphabetical sorting for all symbols
          const sortedSymbols = sortObjAscDescByKey(symbols, 'symbol', ESortType.ASC);
          const { symbolObjects, symbolIds } = this.getSymbolListAndIds(sortedSymbols);

          this.setSymbolsAndIds(symbolObjects, symbolIds, EWatchlistCategory.ALL);
          tradeWatchlistService.setAllWatchlistSymbols(symbolObjects);

          if (selectFirstSymbol || this.pendingSelectFirstSymbolInAll) {
            this.handleSelectFirstSymbol(originalSymbols);
          }
        } else {
          this.setSymbolsAndIds({}, [], EWatchlistCategory.ALL);
        }
      },
      onError: () => {},
      onFinally: () => {
        this.pendingSelectFirstSymbolInAll = false;
        this.processPendingSymbolValidation();
      }
    });

    tradeWatchlistService.setAllSymbolsLoading(false);
    this.isCallingAllSymbolAPI = false;
  };

  private getWatchlistByFilter = async (
    filter: EWatchlistCategory,
    initial?: boolean,
    options?: { allowFallback?: boolean; selectFirstSymbol?: boolean }
  ) => {
    const { allowFallback = true, selectFirstSymbol } = options ?? {};
    this.preLoadWatchlist();

    if (filter === EWatchlistCategory.ALL) {
      this.getAllSymbols(false, selectFirstSymbol);
    } else {
      tradeWatchlistService.setWatchlistLoading(true);

      await watchlistAPIController.getWatchlistWithoutMiniChart(
        { code: filter },
        {
          onSuccess: (symbols) => {
            if (allowFallback && initial && symbols?.length === 0) {
              const fallbackCategory = this.getFallbackWatchlist(filter);
              if (fallbackCategory && fallbackCategory !== EWatchlistCategory.ALL) {
                this.onWatchlistFilterChanged(fallbackCategory);
                this.initial = true;
                return;
              } else if (fallbackCategory === EWatchlistCategory.ALL) {
                this.onWatchlistFilterChanged(EWatchlistCategory.ALL, { selectFirstSymbol: true });
                // Reset the `initial` flag since we are already completed the setup process
                // Else it will causing issue when select another filter if All filter don't have any symbol
                this.initial = false;
                return;
              }
            }

            if (symbols && symbols.length > 0) {
              const { symbolObjects, symbolIds } = this.getSymbolListAndIds(symbols);
              this.setSymbolsAndIds(symbolObjects, symbolIds, this.selectedWatchListFilter);

              if (!initial) {
                return;
              }

              this.handleSelectFirstSymbol(symbols);

              this.initial = false;
            } else {
              this.setSymbolsAndIds({}, [], this.selectedWatchListFilter);
              this.initial = false;
            }
          },
          onError: () => {}
        }
      );

      tradeWatchlistService.setWatchlistLoading(false);
    }
  };

  private handleSelectFirstSymbol = (symbols: ISymbol[]) => {
    if (!symbols || symbols.length === 0) {
      return;
    }

    if (companyViewController.isPC()) {
      this.onSymbolSelected(symbols[0].symbol);
    } else {
      this.handleMobileSymbolSelection(symbols);
    }
  };

  private handleMobileSymbolSelection = (symbols: ISymbol[]) => {
    const lastSelectedSymbol = storage.getItem(EStorageKey.LAST_SELECTED_SYMBOL);

    if (!lastSelectedSymbol) {
      this.onSymbolSelected(symbols[0].symbol);
      return;
    }

    // All symbols already loaded - validate immediately
    if (this.allSymbolIds.length > 0) {
      const isValid = lastSelectedSymbol in this.mappedAllSymbols;
      this.onSymbolSelected(isValid ? lastSelectedSymbol : symbols[0].symbol);
      return;
    }

    // All symbols still loading (from init) - queue validation for when it completes
    this.pendingSymbolValidation = { symbols };
  };

  private processPendingSymbolValidation = () => {
    if (!this.pendingSymbolValidation) {
      return;
    }

    const { symbols } = this.pendingSymbolValidation;
    const lastSelectedSymbol = storage.getItem(EStorageKey.LAST_SELECTED_SYMBOL);

    this.pendingSymbolValidation = null;

    if (lastSelectedSymbol) {
      if (this.allSymbolIds.length === 0) {
        // getAllSymbols failed, can't validate — just use stored symbol as-is
        this.onSymbolSelected(lastSelectedSymbol || symbols[0].symbol);
        return;
      }

      const isValid = lastSelectedSymbol in this.mappedAllSymbols;
      this.onSymbolSelected(isValid ? lastSelectedSymbol : symbols[0].symbol);
    }
  };

  private favouriteSymbol = async (symbol: string, callback?: IFavoriteSymbolCallback) => {
    await watchlistAPIController.favouriteSymbol(
      { symbol },
      {
        onSuccess: () => {
          if (this.selectedWatchListFilter === EWatchlistCategory.FAVOURITES) {
            this.getWatchlistByFilter(this.selectedWatchListFilter);
            this.updateSelectedSymbolWatchState(symbol, true);
          }
          marketViewController.refreshFavourites();
          searchViewController.updateFavouriteSymbolList(symbol, true);
          tradeWatchlistService.setShouldRefetchFavourites(true);

          callback?.onSuccess?.();
        },
        onError: () => {
          callback?.onError?.();
        }
      }
    );
  };

  private unfavouriteSymbol = async (symbol: string, callback?: IFavoriteSymbolCallback) => {
    await watchlistAPIController.unfavouriteSymbol(
      { symbol },
      {
        onSuccess: () => {
          if (this.selectedWatchListFilter === EWatchlistCategory.FAVOURITES) {
            this.getWatchlistByFilter(this.selectedWatchListFilter);
            this.updateSelectedSymbolWatchState(symbol, false);
          }
          marketViewController.refreshFavourites();
          searchViewController.updateFavouriteSymbolList(symbol, false);
          tradeWatchlistService.setShouldRefetchFavourites(true);

          callback?.onSuccess?.();
        },
        onError: () => {
          callback?.onError?.();
        }
      }
    );
  };

  private hasAccessToWatchlistCategory = (category: EWatchlistCategory): boolean => {
    const companyProfile = companyService.context.companyProfile;
    const { hasTopGainerAccess, hasTopLoserAccess, hasTopPickAccess } = validateCompanyAccess(companyProfile);

    switch (category) {
      case EWatchlistCategory.TOP_GAINER:
        return hasTopGainerAccess;
      case EWatchlistCategory.TOP_LOSER:
        return hasTopLoserAccess;
      case EWatchlistCategory.TOP_PICK:
        return hasTopPickAccess;
      default:
        return true;
    }
  };

  private getFallbackWatchlist = (filter: EWatchlistCategory): EWatchlistCategory | undefined => {
    const watchlistCategoryPriority = [
      EWatchlistCategory.FAVOURITES,
      EWatchlistCategory.TOP_PICK,
      EWatchlistCategory.TOP_GAINER,
      EWatchlistCategory.TOP_LOSER,
      EWatchlistCategory.ALL
    ];

    const start = watchlistCategoryPriority.indexOf(filter) + 1;

    for (let i = start; i < watchlistCategoryPriority.length; i++) {
      const category = watchlistCategoryPriority[i];
      if (this.hasAccessToWatchlistCategory(category)) {
        return category;
      }
    }

    return EWatchlistCategory.ALL;
  };

  // Sockets
  public subscribeSymbolInViewport = (symbol: string) => {
    if (!this.symbolToSubscribeSet.has(symbol)) {
      symbolTopicSubscription.subscribe(
        symbol,
        SYM_SUB_KEY_UITRADE_WATCHLIST_ITEM,
        this.updateSymbolPrice,
        undefined,
        this.updateWatchlistMarketStatus
      );
      this.symbolToSubscribeSet.add(symbol);
    }
  };

  public unsubscribeSymbolInViewport = (symbol: string) => {
    if (this.symbolToSubscribeSet.has(symbol)) {
      symbolTopicSubscription.unsubscribe(
        symbol,
        SYM_SUB_KEY_UITRADE_WATCHLIST_ITEM,
        this.updateSymbolPrice,
        undefined,
        this.updateWatchlistMarketStatus
      );
      this.symbolToSubscribeSet.delete(symbol);
    }
  };

  private unsubscribeAllSymbolTopic = () => {
    const symbolsToUnsubscribe = Array.from(this.symbolToSubscribeSet);
    for (const symbol of symbolsToUnsubscribe) {
      symbolTopicSubscription.unsubscribe(
        symbol,
        SYM_SUB_KEY_UITRADE_WATCHLIST_ITEM,
        this.updateSymbolPrice,
        undefined,
        this.updateWatchlistMarketStatus
      );
      this.symbolToSubscribeSet.delete(symbol);
    }
  };

  public initSocketReconnectionListener = () => {
    socketService.addReconnectListener(this.onReconnectSocket);
  };

  public removeSocketReconnectionListener = () => {
    socketService.removeReconnectListener(this.onReconnectSocket);
  };

  // Private functions
  private onReconnectSocket = () => {
    this.getWatchlistByFilter(this.selectedWatchListFilter);
    tradeAnalysisViewController.refetchSymbolDetail();
  };

  private updateSymbolPrice = (id: string, livePrices: ISymbolTopicLivePrices) => {
    const symbol = tradeWatchlistService.context.displayWatchlistSymbols[id];

    if (symbol) {
      tradeWatchlistService.context.displayWatchlistSymbols[id] = {
        ...symbol,
        low: livePrices.liveLow,
        high: livePrices.liveHigh,
        ask: livePrices.liveBuy,
        bid: livePrices.liveSell,
        lastPrice: livePrices.liveLastPrice
      };

      tradeWatchlistService.setDisplayWatchlistSymbols(tradeWatchlistService.context.displayWatchlistSymbols);
    }
  };

  private updateWatchlistMarketStatus = (id: string, isOpen: boolean) => {
    const symbol = tradeWatchlistService.context.displayWatchlistSymbols[id];

    if (symbol) {
      tradeWatchlistService.context.displayWatchlistSymbols[id] = {
        ...symbol,
        online: isOpen
      };

      tradeWatchlistService.setDisplayWatchlistSymbols(tradeWatchlistService.context.displayWatchlistSymbols);
    }
  };

  private allSymbolsWithCategoryFiltered = () => {
    if (this.symbolTypeList.length === 0 || !this.selectedSymbolCategory) {
      tradeWatchlistService.setDisplayWatchlistSymbols(this.mappedAllSymbols);
      tradeWatchlistService.setDisplayWatchlistSymbolIds(this.allSymbolIds);
      return;
    }

    const objects = Object.values(this.mappedAllSymbols).filter(
      (symbol) => symbol.type === this.selectedSymbolCategory
    );

    // Apply alphabetical sorting for filtered symbols
    const sortedObjects = sortObjAscDescByKey(objects, 'symbol', ESortType.ASC);
    const { symbolObjects, symbolIds } = this.getSymbolListAndIds(sortedObjects);
    tradeWatchlistService.setDisplayWatchlistSymbols(symbolObjects);
    tradeWatchlistService.setDisplayWatchlistSymbolIds(symbolIds);
  };

  private preLoadWatchlist = () => {
    switch (this.selectedWatchListFilter) {
      case EWatchlistCategory.ALL:
        this.allSymbolsWithCategoryFiltered();
        break;
      case EWatchlistCategory.FAVOURITES:
        tradeWatchlistService.setDisplayWatchlistSymbols(this.mappedMyWatchlistSymbols);
        tradeWatchlistService.setDisplayWatchlistSymbolIds(this.myWatchlistIds);
        break;
      case EWatchlistCategory.TOP_PICK:
        tradeWatchlistService.setDisplayWatchlistSymbols(this.mappedTopPickSymbols);
        tradeWatchlistService.setDisplayWatchlistSymbolIds(this.topPickIds);
        break;
      case EWatchlistCategory.TOP_GAINER:
        tradeWatchlistService.setDisplayWatchlistSymbols(this.mappedTopGainerSymbols);
        tradeWatchlistService.setDisplayWatchlistSymbolIds(this.topGainerIds);
        break;
      case EWatchlistCategory.TOP_LOSER:
        tradeWatchlistService.setDisplayWatchlistSymbols(this.mappedTopLoserSymbols);
        tradeWatchlistService.setDisplayWatchlistSymbolIds(this.topLoserIds);
        break;
      default:
        break;
    }
  };

  private setSymbolsAndIds = (symbols: Record<string, ISymbol>, ids: string[], filter: EWatchlistCategory) => {
    if (ids.length === 0 && filter !== EWatchlistCategory.ALL) {
      this.reset();

      this.mappedMyWatchlistSymbols = {};
      this.myWatchlistIds = [];
      tradeWatchlistService.setDisplayWatchlistSymbols({});
      tradeWatchlistService.setDisplayWatchlistSymbolIds([]);
      return;
    }

    switch (filter) {
      case EWatchlistCategory.ALL:
        this.mappedAllSymbols = symbols;
        this.allSymbolIds = ids;
        break;
      case EWatchlistCategory.FAVOURITES:
        this.mappedMyWatchlistSymbols = symbols;
        this.myWatchlistIds = ids;
        break;
      case EWatchlistCategory.TOP_PICK:
        this.mappedTopPickSymbols = symbols;
        this.topPickIds = ids;
        break;
      case EWatchlistCategory.TOP_GAINER:
        this.mappedTopGainerSymbols = symbols;
        this.topGainerIds = ids;
        break;
      case EWatchlistCategory.TOP_LOSER:
        this.mappedTopLoserSymbols = symbols;
        this.topLoserIds = ids;
        break;
      default:
        break;
    }

    this.preLoadWatchlist();
  };

  private getSymbolListAndIds = (symbolList: ISymbol[]) => {
    const symbolObjects = symbolList.reduce(
      (objects, symbol) => {
        objects[symbol.symbol] = symbol;
        return objects;
      },
      {} as Record<string, ISymbol>
    );

    const symbolIds = symbolList.map((symbol) => symbol.symbol);

    return { symbolObjects, symbolIds };
  };
}

export const tradeWatchlistViewController = new TradeWatchlistViewController();
